import * as React from 'react';

// components
import Route from 'modules/router';
import AdditionalInfoScreen from '../screens/additional-info';
import EmailVerificationScreen from '../screens/email-verification';
import ErrorScreen from '../screens/error';
import LandingScreen from '../screens/landing/platform-first';
import LoginScreen from '../screens/login';
import PlatformSelectionScreen from '../screens/platform-selection/platform-first';
import SuccessScreen from '../screens/success';

// constants
import { WIDGET_NAME } from '@sitegen/common/constants';
import {
    STEP_LANDING,
    STEP_PLATFORM_SELECTION,
    STEP_REDEMPTION_ERROR,
    STEP_REDEMPTION_SUCCESS,
    STEP_EMAIL_VERIFICATION,
    STEP_ADDITIONAL_INFO,
    STEP_LOGIN,
    STEP_CHECK_STATUS
} from './router.constants';
import {
    REDEMPTION_STATUS,
    SET_REDEMPTION_STATUS,
    RECYCLE_REDEMPTION_PROCESS
} from '../modules/redemption/redemption.constants';
import { SET_LOGIN_SESSION, REMOVE_LOGIN_SESSION, HAS_SESSION_ALREADY } from 'modules/login/login.constants';
import {
    EMAIL_VERIFICATION, SET_EMAIL_VERIFICATION_STATUS
} from 'modules/email-verification/email-verification.constants';
import { EmailVerificationStatusAction } from 'modules/email-verification/email-verification.actions';
import { GO_TO_LOGIN } from '../modules/login/login.constants';
import { IAction } from 'types/action';
import StatusCheck, { MAINTENANCE_CAMPAIGN_ERROR, MAINTENANCE_CAMPAIGN_SUCCESS } from 'modules/status-check';

const endTransitions = {
    [SET_REDEMPTION_STATUS]: {
        [STEP_REDEMPTION_SUCCESS]: (action: IAction) =>
            action.payload.status === REDEMPTION_STATUS.SUCCESS,
        [STEP_ADDITIONAL_INFO]: (action: IAction) =>
            action.payload.status === REDEMPTION_STATUS.ADDITIONAL_INFO,
        [STEP_REDEMPTION_ERROR]: (action: IAction) => ![
            REDEMPTION_STATUS.ADDITIONAL_INFO,
            REDEMPTION_STATUS.SUCCESS
        ].includes(action.payload.status)
    },
    [RECYCLE_REDEMPTION_PROCESS]: { STEP_PLATFORM_SELECTION },
    [REMOVE_LOGIN_SESSION]: { STEP_CHECK_STATUS }
};

export const Router: React.FC = () => {
    return (
        <>
            <Route
                is={STEP_CHECK_STATUS}
                transitions={{
                    [MAINTENANCE_CAMPAIGN_SUCCESS]: { STEP_LANDING },
                    [MAINTENANCE_CAMPAIGN_ERROR]: { STEP_REDEMPTION_ERROR }
                }}
                render={<StatusCheck />}
            />
            <Route
                is={STEP_LANDING}
                transitions={{
                    [GO_TO_LOGIN]: { STEP_LOGIN },
                    [HAS_SESSION_ALREADY]: { STEP_EMAIL_VERIFICATION }
                }}
                render={<LandingScreen/>}
            />
            <Route
                is={STEP_LOGIN}
                transitions={{
                    [SET_LOGIN_SESSION]: { STEP_EMAIL_VERIFICATION }
                }}
                render={<LoginScreen widgetName={WIDGET_NAME.ULC_REDEEM}/>}
            />
            <Route
                is={STEP_EMAIL_VERIFICATION}
                transitions={{
                    [SET_EMAIL_VERIFICATION_STATUS]: {
                        [STEP_PLATFORM_SELECTION]: ( action: EmailVerificationStatusAction ) =>
                            action.payload === EMAIL_VERIFICATION.COMPLETED
                    },
                    [REMOVE_LOGIN_SESSION]: { STEP_LANDING }
                }}
                render={<EmailVerificationScreen/>}
            />
            <Route is={STEP_PLATFORM_SELECTION} transitions={endTransitions} render={<PlatformSelectionScreen/>}/>
            <Route
                is={STEP_REDEMPTION_SUCCESS}
                transitions={{
                    [RECYCLE_REDEMPTION_PROCESS]: { STEP_PLATFORM_SELECTION },
                    [REMOVE_LOGIN_SESSION]: { STEP_LANDING }
                }}
                render={<SuccessScreen/>}
            />
            <Route is={STEP_REDEMPTION_ERROR} transitions={endTransitions} render={<ErrorScreen/>}/>
            <Route
                is={STEP_ADDITIONAL_INFO}
                transitions={{
                    [REMOVE_LOGIN_SESSION]: { STEP_LANDING },
                    [SET_REDEMPTION_STATUS]: {
                        [STEP_REDEMPTION_ERROR]: ({ payload }) =>
                            ![REDEMPTION_STATUS.ADDITIONAL_INFO, REDEMPTION_STATUS.SUCCESS].includes(payload.status),
                        [STEP_REDEMPTION_SUCCESS]: action => action.payload.status === REDEMPTION_STATUS.SUCCESS
                    }
                }}
                render={<AdditionalInfoScreen/>}
            />
        </>
    );
};

Router.displayName = 'Router';

export default Router;
